import { getStafferDirectOffersNoteRuleRetrieveQueryOptions } from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { DirectOfferNoteDialog, type DirectOfferNote } from "domains/admin/components/DirectOfferNoteDialog";
import React from "react";

type Resolve = (note: DirectOfferNote | null) => void;

/**
 * Asks the admin why they are sending a direct offer (CORE-8711), but only for a send to the API's `min_pros`
 * or more (CORE-8902). Resolves to `{}` when no note is needed and to null when they back out.
 */
export function useDirectOfferNoteGate() {
  const queryClient = useQueryClient();
  const resolveRef = React.useRef<Resolve | null>(null);
  const checkingRef = React.useRef(false);
  const [isChecking, setIsChecking] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  // Kept after closing, so the dialog keeps its content while it animates out.
  const [recipients, setRecipients] = React.useState("");
  const [showModeChoice, setShowModeChoice] = React.useState(false);

  const requestNote = async (
    to: string,
    proCount: number,
    gigId?: number,
  ): Promise<Partial<DirectOfferNote> | null> => {
    // A second click during the threshold fetch must not start a second send flow.
    if (checkingRef.current) return null;
    checkingRef.current = true;
    setIsChecking(true);
    // If the threshold can't be read, ask: the note is the safe default.
    const rule = await queryClient
      .query({
        ...getStafferDirectOffersNoteRuleRetrieveQueryOptions(gigId ? { gig_id: gigId } : undefined),
        staleTime: 0,
      })
      .catch(() => null)
      .finally(() => {
        checkingRef.current = false;
        setIsChecking(false);
      });
    if (rule && proCount < rule.min_pros) return {};
    setRecipients(to);
    setShowModeChoice(Boolean(rule?.grants_choice));
    setOpen(true);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  };

  const settle = (note: DirectOfferNote | null) => {
    resolveRef.current?.(note);
    resolveRef.current = null;
    setOpen(false);
  };

  const dialog = (
    <DirectOfferNoteDialog
      open={open}
      recipients={recipients}
      showModeChoice={showModeChoice}
      onCancel={() => settle(null)}
      onConfirm={settle}
    />
  );

  return { requestNote, isChecking, dialog };
}
